import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import {
  DwComponent,
  DwComponentType,
  DwExpressionService,
  DwMetaDataFormApi,
  DwMetaDataFormStateService,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwEventService,
  DwSectionBaseComponent,
  DwUiConfigRegistryService,
  DwUIMetaDataConfig,
  DwUIMetaDataConfigToken
} from '@devwareapps/devware-cap';
import { combineLatest, of, Subject } from 'rxjs';
import { catchError, debounceTime, takeUntil } from 'rxjs/operators';

import {
  AppMetaDataItemNames,
  AssignmentCourseLessonEntity,
  AssignmentEntity,
  CourseEntity,
  CourseLessonEntity,
  RoAssignmentStudentCourseLessonProgressEntity
} from '../../../../meta-data/app-meta-data.service';
import { CourseRepositoryService } from '../../../courses/services/course-repository.service';
import { assignmentContentProgressFilter } from '../../models/assignment-content-progress-filter-event.model';

interface AssignedContentProgressSummary {
  readonly lessonComplete?: boolean;
  readonly percentComplete?: number;
}

interface AssignedContentLessonRow {
  readonly id: number;
  readonly label: string;
  readonly percentComplete: number;
  readonly complete: boolean;
  readonly progressClass: string;
  readonly progressStyle: string;
  readonly progressTitle: string;
}

interface AssignedContentModuleRow {
  readonly id: number;
  readonly label: string;
  readonly assignedLessonCount: number;
  readonly totalLessonCount: number;
  readonly badgeLabel: string;
  readonly percentComplete: number;
  readonly complete: boolean;
  readonly progressClass: string;
  readonly progressStyle: string;
  readonly progressTitle: string;
  expanded: boolean;
  readonly lessons: AssignedContentLessonRow[];
}

@DwComponent({
  key: 'lesson-selection-readonly-list-control',
  name: 'Lesson Selection Readonly List Control',
  componentType: DwComponentType.formSection,
  isGlobal: false,
  parentItemName: AppMetaDataItemNames.Assignment
})
@Component({
  selector: 'app-lesson-selection-readonly-list-form-control',
  templateUrl: './lesson-selection-readonly-list-form-control.component.html',
  styleUrls: ['./lesson-selection-readonly-list-form-control.component.scss']
})
export class LessonSelectionReadonlyListFormControlComponent extends DwSectionBaseComponent implements OnInit, OnDestroy {
  assignedLessons: AbstractControl;
  courseIdFC: FormControl;
  formApi: DwMetaDataFormApi;

  moduleRows: AssignedContentModuleRow[] = [];
  assignedModuleCount = 0;
  assignedLessonCount = 0;
  loading = false;

  private course: CourseEntity;
  private assignmentLessonProgress: RoAssignmentStudentCourseLessonProgressEntity[] = [];
  private assignmentStudentIds: number[] = [];
  private expandedModuleId?: number;
  private readonly controlName = 'AssignmentCourseLesson';
  private readonly destroy$ = new Subject<void>();

  constructor(
    dwExpressionService: DwExpressionService,
    dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    private dwFormStateService: DwMetaDataFormStateService,
    private courseRepositoryService: CourseRepositoryService,
    private dwEventService: DwEventService
  ) {
    super(dwExpressionService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    this.formApi = this.dwFormStateService.state.formApi;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.assignedLessons = this.formGroup.get(this.controlName);
    this.courseIdFC = this.formGroup.get('CourseId') as FormControl;

    if (!this.assignedLessons) {
      console.warn(`Form control ${this.controlName} not found in form group. The assigned content readonly control will use assignment progress rows only.`);
    }

    this.runAfterFormDataLoaded(() => this.loadAssignedContent());

    this.formApi.getFormDataChanges()
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe(() => this.loadAssignedContent());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleModuleExpanded(moduleRow: AssignedContentModuleRow): void {
    const nextExpandedModuleId = moduleRow.expanded ? undefined : moduleRow.id;

    this.expandedModuleId = nextExpandedModuleId;
    this.moduleRows = this.moduleRows.map(row => ({
      ...row,
      expanded: row.id === nextExpandedModuleId
    }));
  }

  filterProgressByModule(moduleRow: AssignedContentModuleRow): void {
    this.publishProgressFilter(moduleRow?.label);
  }

  filterProgressByLesson(lessonRow: AssignedContentLessonRow): void {
    this.publishProgressFilter(lessonRow?.label);
  }

  trackModule(index: number, moduleRow: AssignedContentModuleRow): number {
    return moduleRow.id;
  }

  trackLesson(index: number, lessonRow: AssignedContentLessonRow): number {
    return lessonRow.id;
  }

  private loadAssignedContent(): void {
    const courseId = this.courseIdFC?.value;

    if (!courseId) {
      this.course = null;
      this.assignmentLessonProgress = [];
      this.assignmentStudentIds = [];
      this.buildRows();
      return;
    }

    const assignment = this.formApi.getFormData<AssignmentEntity>();
    const assignmentId = this.resolveAssignmentId(assignment);
    const formAssignmentLessonProgress = this.resolveFormAssignmentLessonProgress(assignment);
    const courseOb = this.course?.CourseId === courseId ? of(this.course) : this.courseRepositoryService.getCourseHierarchy(courseId);
    const assignmentLessonProgressOb = assignmentId
      ? this.courseRepositoryService.loadAssignmentStudentLessonProgress(assignmentId).pipe(catchError(() => of(formAssignmentLessonProgress)))
      : of(formAssignmentLessonProgress);

    this.loading = true;

    combineLatest([courseOb, assignmentLessonProgressOb])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([course, assignmentLessonProgress]) => {
        this.course = course;
        this.assignmentLessonProgress = this.mergeAssignmentLessonProgress(formAssignmentLessonProgress, assignmentLessonProgress || []);
        this.assignmentStudentIds = this.resolveProgressStudentIds(this.assignmentLessonProgress);
        this.loading = false;
        this.buildRows();
      });
  }

  private buildRows(): void {
    const selectedLessonIds = new Set<number>(this.getAssignedLessonIds());
    const moduleRows: AssignedContentModuleRow[] = [];

    for (const courseModule of this.course?.CourseModule || []) {
      const assignedLessons = (courseModule.CourseLesson || []).filter(lesson => selectedLessonIds.has(Number(lesson.CourseLessonId)));

      if (!assignedLessons.length) {
        continue;
      }

      const lessonRows = assignedLessons.map((lesson, index) => this.buildLessonRow(lesson, index));
      const averagePercent = this.calculateAveragePercent(lessonRows.map(lesson => lesson.percentComplete));
      const complete = lessonRows.length > 0 && lessonRows.every(lesson => lesson.complete);
      const isWholeModule = assignedLessons.length === (courseModule.CourseLesson?.length || 0);

      moduleRows.push({
        id: courseModule.CourseModuleId,
        label: courseModule.ModuleName,
        assignedLessonCount: assignedLessons.length,
        totalLessonCount: courseModule.CourseLesson?.length || assignedLessons.length,
        badgeLabel: isWholeModule ? 'Whole Module' : 'Partial',
        percentComplete: averagePercent,
        complete,
        progressClass: this.buildProgressClass(averagePercent),
        progressStyle: this.buildProgressStyle(averagePercent),
        progressTitle: this.buildModuleProgressTitle(averagePercent, lessonRows),
        expanded: this.isModuleExpanded(courseModule.CourseModuleId, moduleRows.length),
        lessons: lessonRows
      });
    }

    this.moduleRows = moduleRows;
    this.assignedModuleCount = moduleRows.length;
    this.assignedLessonCount = moduleRows.reduce((sum, moduleRow) => sum + moduleRow.assignedLessonCount, 0);
  }

  private isModuleExpanded(moduleId: number, moduleIndex: number): boolean {
    if (this.expandedModuleId !== undefined) {
      return this.sameId(this.expandedModuleId, moduleId);
    }

    return moduleIndex === 0;
  }

  private buildLessonRow(
    lesson: CourseLessonEntity,
    assignedIndex: number
  ): AssignedContentLessonRow {
    const lessonProgresses = this.findLessonProgresses(lesson.CourseModuleId, lesson.CourseLessonId);
    const completedStudentCount = lessonProgresses.filter(progress => progress?.lessonComplete || (progress?.percentComplete || 0) >= 100).length;
    const totalStudentCount = Math.max(lessonProgresses.length, this.assignmentStudentIds.length);
    const percentComplete = totalStudentCount
      ? this.calculateAveragePercent(lessonProgresses.map(progress => progress?.lessonComplete ? 100 : progress?.percentComplete || 0))
      : 0;
    const complete = totalStudentCount > 0 && completedStudentCount === totalStudentCount;

    return {
      id: lesson.CourseLessonId,
      label: lesson.LessonName,
      percentComplete,
      complete,
      progressClass: this.buildProgressClass(percentComplete),
      progressStyle: this.buildProgressStyle(percentComplete),
      progressTitle: this.buildProgressTitle(percentComplete, completedStudentCount, totalStudentCount)
    };
  }

  private findLessonProgresses(courseModuleId: number, courseLessonId: number): AssignedContentProgressSummary[] {
    return (this.assignmentLessonProgress || [])
      .filter(progress => this.sameId(progress.CourseModuleId, courseModuleId) && this.sameId(progress.CourseLessonId, courseLessonId))
      .map(progress => ({
        lessonComplete: progress.LessonComplete,
        percentComplete: progress.PercentComplete
      }));
  }

  private getAssignedLessonIds(): number[] {
    const progressLessonIds = (this.assignmentLessonProgress || [])
      .map(progress => Number(progress.CourseLessonId))
      .filter(courseLessonId => !isNaN(courseLessonId))
      .filter((courseLessonId, index, values) => values.indexOf(courseLessonId) === index);

    if (progressLessonIds.length) {
      return progressLessonIds;
    }

    const assignmentLessons: AssignmentCourseLessonEntity[] = this.assignedLessons?.value || [];

    return assignmentLessons
      .filter(lesson => lesson._isDeleted !== true && !!lesson.CourseLessonId)
      .map(lesson => Number(lesson.CourseLessonId));
  }

  private resolveAssignmentId(assignment: AssignmentEntity): number {
    const assignmentId = assignment?.AssignmentId || this.formGroup?.get('AssignmentId')?.value;

    return assignmentId ? Number(assignmentId) : null;
  }

  private resolveFormAssignmentLessonProgress(assignment: AssignmentEntity): RoAssignmentStudentCourseLessonProgressEntity[] {
    return ((assignment as any)?.AssignmentIdRoAssignmentStudentCourseLessonProgress || []) as RoAssignmentStudentCourseLessonProgressEntity[];
  }

  private resolveProgressStudentIds(progressRows: RoAssignmentStudentCourseLessonProgressEntity[]): number[] {
    return (progressRows || [])
      .map(progress => Number(progress.AssignmentStudentId || progress.StudentId))
      .filter(studentId => !isNaN(studentId))
      .filter((studentId, index, values) => values.indexOf(studentId) === index);
  }

  private publishProgressFilter(searchText: string): void {
    if (!searchText) {
      return;
    }

    const assignment = this.formApi.getFormData<AssignmentEntity>();

    this.dwEventService.publishEvent(assignmentContentProgressFilter, {
      searchText,
      assignmentId: this.resolveAssignmentId(assignment),
      requestId: Date.now()
    }, false);
  }

  private mergeAssignmentLessonProgress(
    formProgress: RoAssignmentStudentCourseLessonProgressEntity[],
    loadedProgress: RoAssignmentStudentCourseLessonProgressEntity[]
  ): RoAssignmentStudentCourseLessonProgressEntity[] {
    const mergedProgress = [...(formProgress || [])];

    for (const progress of loadedProgress || []) {
      const existingIndex = mergedProgress.findIndex(existing =>
        this.sameId(existing.StudentId, progress.StudentId) && this.sameId(existing.CourseLessonId, progress.CourseLessonId));

      if (existingIndex >= 0) {
        mergedProgress[existingIndex] = progress;
      } else {
        mergedProgress.push(progress);
      }
    }

    return mergedProgress;
  }

  private sameId(value1: number | string, value2: number | string): boolean {
    return Number(value1) === Number(value2);
  }

  private calculateAveragePercent(percentValues: number[]): number {
    if (!percentValues.length) {
      return 0;
    }

    return this.normalizePercent(percentValues.reduce((sum, value) => sum + value, 0) / percentValues.length);
  }

  private buildProgressStyle(percentComplete: number): string {
    const chunkPercent = this.normalizePercent(Math.round(percentComplete / 25) * 25);

    return `conic-gradient(#78b794 0 ${chunkPercent}%, #edf0f3 ${chunkPercent}% 100%)`;
  }

  private buildProgressClass(percentComplete: number): string {
    if (percentComplete >= 100) {
      return 'assigned-content__progress--complete';
    }

    if (percentComplete > 0) {
      return 'assigned-content__progress--partial';
    }

    return 'assigned-content__progress--empty';
  }

  private buildProgressTitle(percentComplete: number, completedStudentCount: number, totalStudentCount: number): string {
    if (!totalStudentCount) {
      return `${percentComplete}% complete`;
    }

    return `${completedStudentCount} of ${totalStudentCount} students complete - ${percentComplete}% average progress`;
  }

  private buildModuleProgressTitle(percentComplete: number, lessonRows: AssignedContentLessonRow[]): string {
    const completeLessonCount = lessonRows.filter(lesson => lesson.complete).length;

    return `${completeLessonCount} of ${lessonRows.length} lessons complete - ${percentComplete}% average progress`;
  }

  private normalizePercent(percentComplete: number): number {
    return Math.max(0, Math.min(100, Math.round(percentComplete || 0)));
  }
}
